const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/DashboardPage/Components/appointmentCards/utils.ts';
import moment from 'moment';
import { StructuredAppointment } from '@cistec/common_web/defs/kisim';
import { FormatMessageFn } from '@cistec/common_web/defs/generalDefs';
import textMap from './translations';
import _ from 'lodash';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';
import { getPractitioners } from '@cistec/common_web/utils/kisim';

export type TextStuff = {
    termTxt: string | null;
    resName: string | null;
    zustArzt: string | null;
    termStartText: string | null;
    klinikText: string | null;
};

export const fallbackTextStuff: TextStuff = {
    termTxt: null,
    resName: null,
    zustArzt: null,
    termStartText: null,
    klinikText: null,
};

export const getIsSurgeryAppointmentDeadlinePassed = (appointment: StructuredAppointment): boolean => {
    const { EINTRITTDAT, TYP } = appointment;
    const { surgeryAppointmentDeadline } = getConfiguration();

    if (TYP !== 'OPERATION' || !EINTRITTDAT) {
        return false;
    }

    if (surgeryAppointmentDeadline) {
        let appointmentFixedDateTime = moment(EINTRITTDAT, 'DD.MM.YYYY')
            .subtract(surgeryAppointmentDeadline.dayDifference, 'days')
            .set('hour', surgeryAppointmentDeadline.time.hour)
            .set('minute', surgeryAppointmentDeadline.time.minute);

        const appointmentFixedWeekday = appointmentFixedDateTime.isoWeekday();
        const skipToWeekDay = 5; // Friday is the last WorkWeekDay

        if (surgeryAppointmentDeadline.excludeWeekend && appointmentFixedWeekday > skipToWeekDay) {
            appointmentFixedDateTime = appointmentFixedDateTime.subtract(
                appointmentFixedWeekday - skipToWeekDay,
                'day'
            );
        }

        return moment().isAfter(appointmentFixedDateTime);
    } else {
        return true;
    }
};

export const getTextStuff = (appointment: StructuredAppointment, formatMessage: FormatMessageFn): TextStuff => {
    const startdatMoment = moment(appointment.STARTDAT);
    const dateOnlyText = startdatMoment.format('LL');

    const klinik = appointment.CONTENT?.Header?.Klinik;
    const klinikName = klinik?.NAME || null;
    const klinikFkey = klinik?.FKEY || null;
    const klinikText = _.compact([klinikName, klinikName && klinikFkey && `(${klinikFkey})`]).join(' ') || null;

    if (appointment.KAT === 'VORMERKUNG') {
        const getZustArzt = (): string | null => {
            const zustArzt = appointment.CONTENT?.AnmInhalt?.Inhalt?.AngabenAufnahme?.BehandelnderArzt;
            const address = zustArzt?.AddressList?.['1'];

            const zustArztTitle = address?.Titel || null;
            const zustArztName = address?.Name || null;
            const zustArztVorname = address?.Vorname || null;

            if (zustArztTitle && zustArztName && zustArztVorname) {
                return [zustArztTitle, zustArztName, zustArztVorname].join(' ');
            }
            return null;
        };

        return {
            // somehow toString() is required for Typescript... type of RESNAME is string...
            termTxt: appointment.RESNAME?.toString().replace('VORMERKUNG', '').replace('Vormerkung', '') || null,
            resName: appointment.CONTENT?.AnmInhalt?.Inhalt?.AngabenAufnahme?.Zuweiser || null,
            zustArzt: getZustArzt(),
            termStartText: dateOnlyText,
            klinikText,
        };
    } else {
        const termStartText = formatMessage(textMap.termStartText, {
            date: dateOnlyText,
            time: startdatMoment.format('H:mm'),
        });

        switch (appointment.TYP) {
            case 'KONS':
                const terminartForMakro = appointment.CONTENT?.TerminartForMakro;
                const display = appointment.CONTENT?.Display;
                const practitioners = getPractitioners(appointment);
                const practitioner = practitioners?.[0]?.name || undefined;
                return {
                    termTxt: terminartForMakro || display || null,
                    resName: appointment.RESNAME || null,
                    zustArzt: practitioner || null,
                    termStartText,
                    klinikText,
                };
            case 'OPERATION':
                let overrideTermStartText = formatMessage(textMap.dateToBeDefined);
                if (getIsSurgeryAppointmentDeadlinePassed(appointment)) {
                    overrideTermStartText = appointment.EINTRITTDAT_PATIENT ? termStartText : dateOnlyText;
                }
                return {
                    termTxt: 'Operation',
                    resName: null,
                    zustArzt: null,
                    // only if EINTRITTDAT_PATIENT is defined, the whole date and time can be shown
                    termStartText: overrideTermStartText,

                    klinikText,
                };
            case 'BELEGUNG':
                return {
                    ...fallbackTextStuff,
                    termStartText: dateOnlyText,
                    klinikText,
                };
        }
    }

    return fallbackTextStuff;
};
